import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { updateLanguageInHeaders } from "../../network/api";
import { storeuserRole } from "../../Store/TemplateStore/template.action";

import { FEATURE_SCOPE } from "../../constants/global.constants";
import DupontButton from "../DupontButton";
import { API_URLS } from "../../network/appUrls";
import { saveUser } from "../../network/cipAPI";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { ArrowLeftIcon, ROAdvisorLogo, DuPontTMLogo } from "../../assets/icons";
import IconButton from "../../packages/IconButton";
import UserProfile from "./UserProfile";
import createNotification, { ERROR_NOTIFICATION } from "../Notifications";
import PlantMenuHeader from "../PlantMenuHeader";
import RevisitTour from "../RevisitTour";
import UtilityCostsDialog from "../UtilityCosts/UtilityCostsDialog";

import "./index.scss";
const basePath = API_URLS.basePath;

const Header = ({ showPlantMenu, activeStep, isLoading = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    accessUserPlant,
    userName,
    userRole,
    userEmail,
    companyName,
    country,
    currency,
    volume,
    conversion_rate,
  } = useSelector((state) => state);

  const [utilityCostsOpen, setUtilityCostsOpen] = useState(false);
  const [utilityCostsScope, setUtilityCostsScope] = useState(
    FEATURE_SCOPE.USER,
  );
  const [showRevisitHint, setShowRevisitHint] = useState(false);
  const [forceUserProfileOpen, setForceUserProfileOpen] = useState(false);
  const [tourRestartTrigger, setTourRestartTrigger] = useState(0);

  const changeLanguage = async (lng) => {
    i18n.changeLanguage(lng);
    updateLanguageInHeaders(lng);
  };

  const payload = {
    email: userEmail,
    company: companyName,
    country: country,
    role: null,
    industry: null,
  };

  const saveUserDetails = async (payload) => {
    try {
      const response = await saveUser(payload);
      const { user_role } = response;
      dispatch(storeuserRole(`${user_role}`));
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    }
  };

  const onHomeClicked = () => {
    // re fetching user details to get user role, bcz on click of home we are navigating to plants page and in plants page we are checking resetting app's redux store
    saveUserDetails(payload);
    const path = `${basePath}plants`;
    navigate(path);
  };

  const handleBackToUserManagement = () => {
    dispatch({ type: "STORE_ACCESS_USER_PLANT", payload: false });
    navigate("/user-management");
  };

  const handleManageAccess = () => {
    navigate(`${basePath}admin-portal`);
  };

  const handleManageUser = () => {
    navigate(`${basePath}user-management`);
  };

  const openUtilityCosts = (scope) => {
    setUtilityCostsScope(scope);
    setUtilityCostsOpen(true);
  };

  const handleUserUtilityCosts = () => {
    openUtilityCosts(FEATURE_SCOPE.USER);
  };

  const handleProjectUtilityCosts = () => {
    openUtilityCosts(FEATURE_SCOPE.PLANT);
  };

  const handleUtilityCostsClose = () => {
    setUtilityCostsOpen(false);
  };

  const isPlantScope = utilityCostsScope === FEATURE_SCOPE.PLANT;
  const isAddOrEditPlantPage =
    location.pathname.includes("addPlant") ||
    location.pathname.includes("editPlant");

  const shouldEnablePendingUnitConversion =
    isPlantScope && isAddOrEditPlantPage;

  const isPathIncluded = (paths) => {
    return Array.isArray(paths)
      ? paths.some((path) => location.pathname.includes(path))
      : location.pathname.includes(paths);
  };

  const handleAvatarHover = () => {
    saveUserDetails(payload);
  };

  const handleRevisitTourClick = () => {
    setShowRevisitHint(false);
    setForceUserProfileOpen(false);
    setTourRestartTrigger((prev) => prev + 1);
  };

  return (
    <>
      <div className="header-container">
        <div className="header-left-section">
          {accessUserPlant && (
            <IconButton
              tooltip={t("addNewPlant.backUserManagement")}
              placement="bottom"
            >
              <WPTertiaryButton
                icon={<ArrowLeftIcon />}
                className="back-user-management-btn"
                onClick={handleBackToUserManagement}
              />
            </IconButton>
          )}

          <DuPontTMLogo
            onClick={onHomeClicked}
            className="header-dupont-logo"
          />
          <ROAdvisorLogo />
          <div className="header-title">{t("header.title")}</div>
        </div>
        <div className="header-right-section">
          {!isPathIncluded(["messagePage", "requestAccess"]) && userEmail && (
            <>
              {showPlantMenu && (
                <RevisitTour
                  showtooltipAfterTour={showRevisitHint}
                  onClick={handleRevisitTourClick}
                />
              )}
              <DupontButton
                type="tertiary"
                customClass="lang-btn"
                onClick={() => changeLanguage("en")}
                disabled={activeStep && activeStep > 0}
              >
                en
              </DupontButton>
              <DupontButton
                type="tertiary"
                customClass="lang-btn chinese-btn"
                onClick={() => changeLanguage("zh")}
                disabled={activeStep && activeStep > 0}
              >
                中文
              </DupontButton>
              <UserProfile
                userName={userName}
                userEmail={userEmail}
                userRole={userRole}
                onManageAccess={handleManageAccess}
                onManageUser={handleManageUser}
                isPathIncluded={isPathIncluded}
                onAvatarHover={handleAvatarHover}
                onUtilityCosts={handleUserUtilityCosts}
                isForcedOpen={forceUserProfileOpen}
              />
            </>
          )}
        </div>
        {utilityCostsOpen && (
          <UtilityCostsDialog
            open
            scope={utilityCostsScope}
            currency={currency}
            volume={volume}
            conversionRate={conversion_rate}
            enablePendingUnitConversion={shouldEnablePendingUnitConversion}
            onClose={handleUtilityCostsClose}
          />
        )}
      </div>
      {showPlantMenu && (
        <PlantMenuHeader
          onCostsClick={handleProjectUtilityCosts}
          setShowRevisitHint={setShowRevisitHint}
          setForceUserProfileOpen={setForceUserProfileOpen}
          tourRestartTrigger={tourRestartTrigger}
          currentPage={location.pathname}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default Header;
